import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';

interface BudgetEditModalProps {
    show: boolean;
    onHide: () => void;
    onSave: (amount: number) => Promise<void>;
    currentAmount?: number;
    submitting: boolean;
}

export const BudgetEditModal: React.FC<BudgetEditModalProps> = ({
    show,
    onHide,
    onSave,
    currentAmount,
    submitting,
}) => {
    const [amount, setAmount] = useState('');
    const [error, setError] = useState<string | null>(null);

    // Populate field when modal opens
    useEffect(() => {
        if (show) {
            setAmount(currentAmount ? currentAmount.toString() : '');
            setError(null);
        }
    }, [show, currentAmount]);

    const handleSave = async () => {
        const parsed = parseInt(amount, 10);

        if (isNaN(parsed) || parsed < 1) {
            setError('Ange ett positivt heltal');
            return;
        }

        try {
            await onSave(parsed);
            onHide();
        } catch (err) {
            console.error('Error saving budget:', err);
            setError('Kunde inte spara. Försök igen.');
        }
    };

    const handleClose = () => {
        setAmount('');
        setError(null);
        onHide();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !submitting) {
            handleSave();
        }
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Sätt årsbudget</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Group>
                    <Form.Label>Budget för {new Date().getFullYear()} (SEK)</Form.Label>
                    <Form.Control
                        type="number"
                        value={amount}
                        onChange={(e) => {
                            setAmount(e.target.value);
                            setError(null);
                        }}
                        onKeyDown={handleKeyDown}
                        disabled={submitting}
                        placeholder="t.ex. 5000000"
                        min={1}
                        step={1}
                        autoFocus
                        isInvalid={!!error}
                    />
                    {error && (
                        <Form.Control.Feedback type="invalid">
                            {error}
                        </Form.Control.Feedback>
                    )}
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={submitting}>
                    Avbryt
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={submitting || !amount}
                >
                    {submitting ? 'Sparar...' : 'Spara'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
